/**
 * Catches render-time errors below the routed Suspense — most importantly,
 * rejected dynamic-import promises. The "perpetual Loading…" bug we hit
 * after a Heroku redeploy was a stale chunk hash 404ing; Suspense holds
 * the fallback forever because Suspense alone has no error path.
 *
 * Strategy: on a chunk-load error, reload once per session (sessionStorage
 * flag) — the fresh index.html points at the current chunk hashes, so a
 * single reload recovers cleanly. On any other error, or a chunk error
 * after we've already auto-reloaded, show a Reload button.
 */
import { Component, type ReactNode } from 'react'

const RELOAD_FLAG = 'engage:chunk-reload'

interface State {
  error: Error | null
}

interface Props {
  children: ReactNode
}

function isChunkLoadError(err: Error): boolean {
  if (err.name === 'ChunkLoadError') return true
  return /Failed to fetch dynamically imported module|Loading chunk \d+ failed|Importing a module script failed/i.test(
    err.message ?? '',
  )
}

export class RouteBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error): void {
    if (isChunkLoadError(error) && sessionStorage.getItem(RELOAD_FLAG) !== '1') {
      sessionStorage.setItem(RELOAD_FLAG, '1')
      window.location.reload()
    }
  }

  handleReload = (): void => {
    sessionStorage.removeItem(RELOAD_FLAG)
    window.location.reload()
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children

    if (isChunkLoadError(this.state.error) && sessionStorage.getItem(RELOAD_FLAG) !== '1') {
      return (
        <main className="mx-auto flex min-h-screen max-w-2xl items-center justify-center px-6">
          <p style={{ color: 'var(--color-text-muted)' }}>Loading…</p>
        </main>
      )
    }

    return (
      <main className="mx-auto flex min-h-screen max-w-2xl items-center justify-center px-6 text-center">
        <div className="flex flex-col items-center gap-4">
          <p
            className="text-xs uppercase tracking-[0.18em]"
            style={{ color: 'var(--color-text-muted)' }}
          >
            Engage
          </p>
          <p className="text-base" style={{ color: 'var(--color-text)' }}>
            Something interrupted the page. Reload to try again.
          </p>
          <button
            onClick={this.handleReload}
            className="rounded-md px-4 py-2 text-sm font-semibold text-white transition"
            style={{ background: 'var(--color-primary)' }}
          >
            Reload
          </button>
        </div>
      </main>
    )
  }
}
